import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Film, Award } from "lucide-react";
import { Reveal } from "./Reveal";
import { VIDEOS, CREATIVE_INTRO, PHOTOGRAPHY } from "../../data/content";

export const Photography = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="creative" data-testid="creative-section" className="bg-sand border-y border-line">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-24 md:py-40">
        <Reveal className="max-w-4xl">
          <p className="overline text-terracotta flex items-center gap-2">
            <Camera size={14} /> Photography &amp; Videography
          </p>
          <h2 className="mt-6 font-serif text-5xl md:text-7xl tracking-tight leading-[0.95] text-ink">
            Two decades
            <br />
            behind the camera.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-stone leading-relaxed font-light border-l-2 border-terracotta pl-5 md:pl-6">
            {CREATIVE_INTRO}
          </p>
          <div
            data-testid="photography-chair-role"
            className="mt-8 flex items-start gap-4 rounded-2xl border border-line bg-paper p-6"
          >
            <span className="grid place-items-center w-11 h-11 shrink-0 rounded-full bg-ink text-paper">
              <Award size={18} />
            </span>
            <div>
              <p className="font-serif text-xl md:text-2xl text-ink leading-tight">
                Chair of Media, Communications &amp; Digital Innovation
              </p>
              <p className="mt-1 text-sm md:text-base text-stone leading-relaxed">
                Timbaland Way Cares &middot; current role leading media, storytelling and digital
                strategy for the organization.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Photography */}
        <Reveal delay={0.05}>
          <div className="mt-20 flex items-center gap-4">
            <Camera size={20} className="text-terracotta" />
            <p className="overline text-stone">Photography</p>
            <span className="h-px flex-1 bg-line" />
          </div>
        </Reveal>
        <div className="mt-8 columns-1 sm:columns-2 lg:columns-3 gap-5 md:gap-6">
          {PHOTOGRAPHY.map((p, i) => (
            <Reveal key={p.key} delay={(i % 3) * 0.06} className="mb-5 md:mb-6 break-inside-avoid">
              <button
                onClick={() => setActive(p)}
                data-testid={`photo-${p.key}`}
                className="group relative block w-full overflow-hidden rounded-2xl border border-line bg-sand"
              >
                <img
                  src={p.src}
                  alt={p.tag}
                  loading="lazy"
                  className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/0 to-ink/0 opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
                <div className="absolute left-5 bottom-5 right-5 text-left">
                  <p className="text-[0.65rem] uppercase tracking-[0.18em] text-paper/80">{p.tag}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {/* Videography */}
        <Reveal delay={0.05}>
          <div className="mt-24 flex items-center gap-4">
            <Film size={20} className="text-terracotta" />
            <p className="overline text-stone">Videography</p>
            <span className="h-px flex-1 bg-line" />
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {VIDEOS.map((v, i) => (
            <Reveal key={v.key} delay={i * 0.08}>
              <div
                data-testid={`video-${v.key}`}
                className="group overflow-hidden rounded-2xl border border-line bg-ink"
              >
                <video
                  src={v.src}
                  poster={v.poster}
                  controls
                  preload="none"
                  playsInline
                  className="w-full h-auto max-h-[70vh] bg-black"
                />
                <div className="flex items-center justify-between px-5 py-4 bg-paper">
                  <p className="font-serif text-xl text-ink tracking-tight">{v.title}</p>
                  <p className="text-[0.65rem] uppercase tracking-[0.16em] text-stone">{v.tag}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-14 font-serif italic text-xl md:text-2xl text-stone">
            More event and portrait work available on request.
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="creative-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/90 backdrop-blur-md p-4 md:p-10"
          >
            <button
              data-testid="creative-modal-close"
              onClick={() => setActive(null)}
              className="absolute right-5 top-5 grid place-items-center w-11 h-11 rounded-full bg-paper text-ink hover:bg-terracotta hover:text-paper transition-colors duration-300"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              src={active.src}
              alt={active.title}
              className="max-h-[85vh] max-w-full w-auto rounded-xl object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
